import { z } from 'zod';

import { sessionSchema, type Session } from '@chariot/core-contracts';

/**
 * `/api/session` is parsed against the shared contract (architecture/04 —
 * "useSession is the only source of identity in the browser"). Parsed, not
 * trusted: a drift between the auth function and the browser fails loudly here.
 */
export { sessionSchema, type Session };

export const SESSION_URL = '/api/session';
export const LOGIN_URL = '/api/auth/login';
export const LOGOUT_URL = '/api/auth/logout';

/** `null` is "not signed in" — a 401 is an answer, not an error (04: a 401 anywhere returns to sign-in). */
export async function fetchSession(fetchImpl: typeof fetch = fetch): Promise<Session | null> {
  const response = await fetchImpl(SESSION_URL, {
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  });

  if (response.status === 401) return null;
  if (!response.ok) throw new Error(`Session request failed with ${String(response.status)}`);

  return sessionSchema.parse(await response.json());
}

/** Where the browser goes to start sign-in. `provider` names an identity provider on the pool, such as `GoogleWorkspace`. */
export function loginHref(options: { provider?: string; returnTo?: string } = {}): string {
  const params = new URLSearchParams();
  if (options.provider !== undefined) params.set('provider', options.provider);
  if (options.returnTo !== undefined) params.set('returnTo', options.returnTo);
  const query = params.toString();
  return query.length === 0 ? LOGIN_URL : `${LOGIN_URL}?${query}`;
}

const signOutResponseSchema = z.object({ logoutUrl: z.url().nullable() });

export interface SignOutResult {
  /**
   * Cognito's hosted-UI logout, which the browser must visit to end the
   * identity provider's session too; `null` where the pool has none (LocalStack).
   */
  readonly logoutUrl: string | null;
}

export async function signOut(fetchImpl: typeof fetch = fetch): Promise<SignOutResult> {
  const response = await fetchImpl(LOGOUT_URL, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`Sign-out failed with ${String(response.status)}`);
  return signOutResponseSchema.parse(await response.json());
}
